import { type Booking, BookingStatus, type Location, LocationKind, MeetingMode, type ReminderInfo } from '../../models/Scheduling'
import { getDateTimeFormatter } from '../../common/intlFormatters'

const TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true }
const WEEKDAY_OPTIONS: Intl.DateTimeFormatOptions = { weekday: 'short' }
const BOOKING_DATE_OPTIONS: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
const INVITE_DATE_OPTIONS: Intl.DateTimeFormatOptions = { weekday: 'long', month: 'long', day: 'numeric' }

const timeFormatter = (timeZone?: string) => getDateTimeFormatter(TIME_OPTIONS, timeZone)
const inviteDateFormatter = (timeZone?: string) => getDateTimeFormatter(INVITE_DATE_OPTIONS, timeZone)
const bookingDateFormatter = () => getDateTimeFormatter(BOOKING_DATE_OPTIONS)

// "APR 24, 2026" — the uppercase date shown in a booking row's time block
export const formatBookingDate = (date: Date): string => bookingDateFormatter().format(date).toUpperCase()

// "Apr 24, 2026" — the (always year-qualified) date under the Waiting "sent ago" label
export const formatSentDate = (date: Date): string => bookingDateFormatter().format(date)

// Split the formatted time into its clock value and day period via formatToParts, so we never
// depend on the literal separator Intl emits (recent ICU uses a narrow no-break space, not ' ').
const formatTimeParts = (date: Date, timeZone?: string): { time: string; period: string } => {
  const parts = timeFormatter(timeZone).formatToParts(date)
  const time = parts
    .filter((part) => part.type === 'hour' || part.type === 'minute' || (part.type === 'literal' && part.value === ':'))
    .map((part) => part.value)
    .join('')
  const period = parts.find((part) => part.type === 'dayPeriod')?.value ?? ''
  return { time, period }
}

// "2:00 – 3:00 PM" (the shared day period is only printed once when it matches)
export const formatTimeRange = (start: string, end: string, timeZone?: string): string => {
  const startParts = formatTimeParts(new Date(start), timeZone)
  const endParts = formatTimeParts(new Date(end), timeZone)
  const endFormatted = `${endParts.time} ${endParts.period}`
  return startParts.period === endParts.period ? `${startParts.time} – ${endFormatted}` : `${startParts.time} ${startParts.period} – ${endFormatted}`
}

// Parse a date-only ISO string ("2026-07-28") as a local calendar date; new Date() would read it
// as UTC midnight and shift it a day back in negative-offset timezones.
export const parseISODateAsLocal = (isoDate: string): Date => {
  const [year, month, day] = isoDate.split('-').map(Number)
  return new Date(year, month - 1, day)
}

interface SuggestedTimeInput {
  start?: string
  end?: string
  suggestedDate?: string
  noTimeLabel?: string
}

// Inline date/time shown next to the calendar icon on Suggested rows: "Jul 1, 2026 · 10:00 – 11:00 AM".
// Falls back to the start date alone, then to the AI-suggested date ("Jul 28, 2026"), then to a "no time" label.
export const formatSuggestedTime = ({ start, end, suggestedDate, noTimeLabel = 'No time discussed' }: SuggestedTimeInput): string => {
  if (start) {
    return formatDateAndTimeRange(start, end)
  }
  if (suggestedDate) {
    const day = parseISODateAsLocal(suggestedDate)
    if (!Number.isNaN(day.getTime())) {
      return bookingDateFormatter().format(day)
    }
  }
  return noTimeLabel
}

// "THU"
export const formatWeekdayShort = (date: Date): string => getDateTimeFormatter(WEEKDAY_OPTIONS).format(date).toUpperCase()

// "9:00 AM" — a single clock time, joining the value parts so we never depend on the
// literal separator Intl emits (recent ICU uses a narrow no-break space).
export const formatTime = (date: Date, timeZone?: string): string => {
  const { time, period } = formatTimeParts(date, timeZone)
  return `${time} ${period}`
}

// "Apr 24, 2026 · 10:06 AM" — a full date plus a single clock time, as shown on Archive rows
export const formatDateAndTime = (date: Date): string => `${formatSentDate(date)} · ${formatTime(date)}`

// "Apr 25, 2026 · 12:30 – 1:30 PM", or just the date + start time when there's no end —
// the shared date-plus-time shape of Suggested rows and Archive time lines
export const formatDateAndTimeRange = (start: string, end?: string): string =>
  end ? `${formatSentDate(new Date(start))} · ${formatTimeRange(start, end)}` : formatDateAndTime(new Date(start))

// "Friday, June 21 · 9:00 AM" — the WHEN line of the calendar-invite preview
export const formatInviteWhen = (start: string, timeZone?: string): string => {
  const date = new Date(start)
  return `${inviteDateFormatter(timeZone).format(date)} · ${formatTime(date, timeZone)}`
}

const LOCATION_KIND_LABELS: Partial<Record<LocationKind, string>> = {
  [LocationKind.ACCOUNT_DEFAULT]: 'Account default',
  [LocationKind.USER_DEFAULT]: 'User default',
}

export const getBookingLocationLine = (booking: Booking, location: Location | undefined): string | undefined => {
  if (booking.meetingMode !== MeetingMode.IN_PERSON) {
    return undefined
  }
  const kindLabel = booking.location ? LOCATION_KIND_LABELS[booking.location.kind] : undefined
  const isUnconfirmed = booking.status !== BookingStatus.CONFIRMED && booking.status !== BookingStatus.CANCELLED
  if (kindLabel && isUnconfirmed) {
    return location ? `${kindLabel} · ${location.name}` : kindLabel
  }
  return location?.address
}

// "812 4471 0093" — Zoom join URLs carry the numeric meeting id in the /j/<id> path segment,
// displayed with Zoom's own 3-4-4 (11 digits) / 3-3-4 (10 digits) grouping. Google Meet uses the
// xxx-yyyy-zzz room code as its meeting id. Teams links carry no human-readable id.
export const getMeetingIdFromUrl = (meetingUrl: string): string | undefined => {
  let pathname: string
  try {
    pathname = new URL(meetingUrl).pathname
  } catch {
    return undefined
  }
  const zoomId = /\/j\/(\d{9,11})(?:\/|$)/.exec(pathname)?.[1]
  if (zoomId) {
    if (zoomId.length === 11) {
      return `${zoomId.slice(0, 3)} ${zoomId.slice(3, 7)} ${zoomId.slice(7)}`
    }
    if (zoomId.length === 10) {
      return `${zoomId.slice(0, 3)} ${zoomId.slice(3, 6)} ${zoomId.slice(6)}`
    }
    return zoomId
  }
  return /^\/([a-z]{3}-[a-z]{4}-[a-z]{3})$/.exec(pathname)?.[1]
}

const MILLISECONDS_IN_A_DAY = 24 * 60 * 60 * 1000

const calendarDayDiff = (from: Date, to: Date): number => {
  const fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const toDay = new Date(to.getFullYear(), to.getMonth(), to.getDate())
  return Math.round((toDay.getTime() - fromDay.getTime()) / MILLISECONDS_IN_A_DAY)
}

// "SENT 13 DAYS AGO" / "SENT TODAY" labels for Waiting rows
export const getSentAgoLabel = (sentAt: string, now: Date = new Date()): string => {
  const daysAgo = calendarDayDiff(new Date(sentAt), now)
  if (daysAgo <= 0) {
    return 'SENT TODAY'
  }
  if (daysAgo === 1) {
    return 'SENT YESTERDAY'
  }
  return `SENT ${daysAgo} DAYS AGO`
}

export const getReminderLabel = (reminder: ReminderInfo | undefined): string | null => {
  const count = reminder?.count ?? 0
  if (count === 0) {
    return null
  }
  return count === 1 ? '1 reminder sent' : `${count} reminders sent`
}
